import React, { useEffect, useState } from "react";
import { Check, Clock, UserPlus, UserCheck, Undo2, Trash2, PackageCheck } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "./ui/sheet";
import SegmentedField from "./SegmentedField";
import EditableCue from "./EditableCue";
import { SnoozeChips } from "./SnoozeChips";
import { useAuth } from "../contexts/AuthContext";
import {
  TYPE_OPTIONS, HOUSEHOLD_DOMAIN_OPTIONS, TYPE_META, DOMAIN_META,
  VISIBILITY_OPTIONS, VISIBILITY_META, handoffAction,
} from "../lib/meta";

const opts = (arr, meta) => arr.map((v) => ({ value: v, label: meta[v].label }));
const tz = () => -new Date().getTimezoneOffset();

const REASON_CHIPS = [
  "I can't do this today",
  "Can you take this one?",
  "Need more info",
  "Not mine",
];

const actionBtn =
  "inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-200";

// Considered detail/edit sheet for a single item. Preserves the original captured
// text as read-only audit; everything else is editable. Reuses existing fields and
// update behaviour — no classifier change, no new data model.
export default function ItemDetailSheet({ entry, open, onOpenChange, onChange, onDelete, initialHandoff, relatedAnchor }) {
  const { user } = useAuth();
  const hasPartner = Boolean(user?.partner);
  const partnerName = user?.partner?.name || "your partner";
  const [title, setTitle] = useState("");
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [ov, setOv] = useState({});  // optimistic overrides so edits reflect inline
  const [reasonAction, setReasonAction] = useState(null);  // null | "partner" | "me"
  const [reasonChip, setReasonChip] = useState("");
  const [reasonNote, setReasonNote] = useState("");

  useEffect(() => {
    setSnoozeOpen(false);
    setOv({});
    setReasonChip("");
    setReasonNote("");
    // When launched from the card overflow menu, open straight into the matching
    // handoff/return reason panel (reason stays optional). Otherwise start closed.
    setReasonAction(open ? (initialHandoff || null) : null);
    if (entry) setTitle(entry.interpreted_summary || "");
  }, [entry?.id, open, initialHandoff]);

  if (!entry) return null;

  const e = { ...entry, ...ov };
  const done = e.status === "DONE";
  const ho = handoffAction(e, partnerName);
  const HandoffIcon = ho.kind === "give" ? UserPlus : Undo2;
  const domainValue = e.domain === "PROSPECT" ? "WORK" : e.domain;

  // Merge real entry fields (and any explicit extras) into local state instantly,
  // then persist via onChange. tz_offset/clear_cue are API-only, not entry fields.
  const change = (changes, extra = {}) => {
    const { tz_offset, clear_cue, ...rest } = changes;
    setOv((p) => ({ ...p, ...rest, ...extra }));
    return onChange(entry.id, changes);
  };
  const commitTitle = () => {
    const v = title.trim();
    if (v && v !== e.interpreted_summary) change({ interpreted_summary: v });
  };

  const openReason = (action) => { setReasonAction(action); setReasonChip(""); setReasonNote(""); };
  const cancelReason = () => { setReasonAction(null); setReasonChip(""); setReasonNote(""); };
  const confirmReason = () => {
    const reason = reasonNote.trim() || reasonChip || undefined;  // custom note wins; reason optional
    change({ owner: reasonAction, handoff_reason: reason },
           { handoff_note_line: null });  // clear any stale note line optimistically
    cancelReason();
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="bg-[var(--surface3)] border-t border-[var(--line)] rounded-t-3xl max-h-[90vh] overflow-y-auto p-0 gap-0"
        data-testid="item-detail-sheet"
      >
        <div className="mx-auto w-10 h-1.5 rounded-full bg-[var(--pine-line)] mt-3 mb-1" />
        <SheetTitle className="sr-only">Item details</SheetTitle>
        <SheetDescription className="sr-only">View and edit this item: its title, original text, timing, and actions.</SheetDescription>

        <div className="px-5 md:px-7 sheet-safe-bottom pt-3 space-y-6 max-w-2xl mx-auto w-full">
          {/* Status / handoff line */}
          <div className="flex flex-wrap items-center gap-2">
            {done && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--pine-soft)] text-[var(--pine)] border border-[var(--pine-line)] px-2.5 py-0.5 text-xs font-semibold" data-testid="detail-done-state">
                <Check size={12} strokeWidth={2.2} /> Done
              </span>
            )}
          </div>

          {/* Sender-facing confirmation (Pass 2 v1): Landed with / marked this done */}
          {e.confirmation_line && (
            <div
              data-testid="detail-confirmation"
              className="rounded-xl bg-[var(--pine-soft)] border border-[var(--pine-line)] px-3.5 py-2.5 text-sm text-[var(--pine)] flex items-start gap-2"
            >
              {e.status === "DONE"
                ? <Check size={15} strokeWidth={2.4} className="mt-0.5 shrink-0" />
                : <PackageCheck size={15} strokeWidth={1.9} className="mt-0.5 shrink-0" />}
              <span>{e.confirmation_line}</span>
            </div>
          )}

          {/* Latest handoff/return note for this viewer — calm, factual, no thread/read-state */}
          {e.handoff_note_line && (
            <div
              data-testid="detail-handoff-note"
              className="rounded-xl bg-[var(--slate-soft)] border border-[var(--slate-soft)] px-3.5 py-2.5 text-sm text-[var(--slate)] flex items-start gap-2"
            >
              <Undo2 size={15} strokeWidth={1.9} className="mt-0.5 shrink-0" />
              <span>{e.handoff_note_line}</span>
            </div>
          )}

          {/* Ownership — prominent: who it's with right now + the one obvious handoff action */}
          {hasPartner && !done && (
            <div
              data-testid="detail-ownership"
              className="rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3.5"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${
                    e.owner === "partner" ? "bg-[var(--slate-soft)] text-[var(--slate)]" : "bg-[var(--pine-soft)] text-[var(--pine)]"}`}>
                    <UserCheck size={18} strokeWidth={1.9} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[11px] uppercase tracking-[0.16em] font-bold text-[var(--ink5)]">With</p>
                    <p className="text-[15px] font-medium text-[var(--ink)] truncate" data-testid="detail-owner-name">
                      {e.owner === "partner" ? partnerName : "You"}
                    </p>
                  </div>
                </div>
                {ho.action === "me" ? (
                  <button
                    data-testid="detail-takeback-btn"
                    onClick={() => openReason("me")}
                    className={`${actionBtn} shrink-0 bg-[var(--surface)] text-[var(--ink2)] border border-[var(--line)] hover:border-[var(--pine)] hover:text-[var(--pine)]`}
                  >
                    <HandoffIcon size={15} strokeWidth={2} /> {ho.label}
                  </button>
                ) : (
                  <button
                    data-testid="detail-giveto-partner-btn"
                    onClick={() => openReason("partner")}
                    className={`${actionBtn} shrink-0 bg-[var(--pine)] text-[var(--on-pine)] hover:bg-[var(--pine-dark)] shadow-[0_6px_18px_rgba(44,90,77,0.22)]`}
                  >
                    <HandoffIcon size={15} strokeWidth={2} /> {ho.label}
                  </button>
                )}
              </div>

              {/* Optional reason step (action-based, not chat): preset chips + one short note */}
              {reasonAction && (
                <div className="mt-4 pt-4 border-t border-[var(--line2)] rise-in" data-testid="handoff-reason-panel">
                  <p className="text-[13px] text-[var(--ink3)] mb-2.5">
                    Add a reason <span className="text-[var(--ink5)]">(optional)</span>
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {REASON_CHIPS.map((chip) => (
                      <button
                        key={chip}
                        type="button"
                        data-testid={`handoff-reason-chip-${REASON_CHIPS.indexOf(chip)}`}
                        onClick={() => setReasonChip((c) => (c === chip ? "" : chip))}
                        className={`rounded-full px-3 py-1.5 text-[13px] font-medium border transition-all duration-200 ${
                          reasonChip === chip
                            ? "bg-[var(--pine)] text-[var(--on-pine)] border-[var(--pine)]"
                            : "bg-[var(--surface)] text-[var(--ink2)] border-[var(--line)] hover:border-[var(--pine)]"}`}
                      >
                        {chip}
                      </button>
                    ))}
                  </div>
                  <input
                    data-testid="handoff-reason-note"
                    value={reasonNote}
                    onChange={(ev) => setReasonNote(ev.target.value)}
                    maxLength={80}
                    placeholder="Or add a short note"
                    className="mt-3 w-full rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 text-sm text-[var(--ink)] outline-none focus:border-[var(--pine)] focus:ring-2 focus:ring-[var(--pine-soft)] transition-all"
                  />
                  <div className="flex items-center gap-2 mt-3">
                    <button
                      data-testid="handoff-reason-confirm"
                      onClick={confirmReason}
                      className={`${actionBtn} bg-[var(--pine)] text-[var(--on-pine)] hover:bg-[var(--pine-dark)]`}
                    >
                      {reasonAction === "me" ? "Take it back" : ho.label}
                    </button>
                    <button
                      data-testid="handoff-reason-cancel"
                      onClick={cancelReason}
                      className={`${actionBtn} bg-transparent text-[var(--ink3)] hover:text-[var(--ink2)]`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Editable cleaned title */}
          <div>
            <label className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ink5)]">Title</label>
            <textarea
              data-testid="detail-title-input"
              value={title}
              onChange={(ev) => setTitle(ev.target.value)}
              onBlur={commitTitle}
              onKeyDown={(ev) => { if (ev.key === "Enter") { ev.preventDefault(); ev.target.blur(); } }}
              rows={2}
              className="mt-1.5 w-full resize-none rounded-xl border border-[var(--line)] bg-[var(--surface)] px-3.5 py-2.5 font-serif-display text-xl leading-snug text-[var(--ink)] outline-none focus:border-[var(--pine)] focus:ring-2 focus:ring-[var(--pine-soft)] transition-all"
            />
          </div>

          {relatedAnchor && (
            <p className="text-[13px] text-[var(--ink3)]" data-testid="detail-related-anchor">
              Related to: {relatedAnchor.interpreted_summary}
              {relatedAnchor.suggested_datetime
                ? ` (${new Date(relatedAnchor.suggested_datetime).toLocaleDateString(undefined, { day: "numeric", month: "long" })})`
                : ""}.
            </p>
          )}

          {/* Original captured text — read-only audit */}
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ink5)] mb-1">You said</p>
            <p className="text-[15px] leading-relaxed text-[var(--ink2)] bg-[var(--bg)] border border-[var(--line)] rounded-xl px-3.5 py-3" data-testid="detail-raw-text">
              “{e.raw_text}”
            </p>
          </div>

          {/* Timing / cue — only meaningful while the item is still active */}
          {!done && (
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--ink5)] mb-2">When it comes back</p>
              <EditableCue
                entry={e}
                testid="detail-cue"
                onApply={(iso) => iso === null
                  ? change({ clear_cue: true, tz_offset: tz() }, { suggested_datetime: null, timing_status: "none", timing_phrase: null, needs_review: false })
                  : change({ suggested_datetime: iso, tz_offset: tz() }, { suggested_datetime: iso, timing_status: "resolved", timing_phrase: null, needs_review: false })}
              />
            </div>
          )}

          {/* Classification */}
          <div className="space-y-3">
            <SegmentedField label="Type" testid="detail-type" value={e.type}
              options={opts(TYPE_OPTIONS, TYPE_META)} onChange={(v) => change({ type: v })} />
            <SegmentedField label="Area" testid="detail-domain" value={domainValue}
              options={opts(HOUSEHOLD_DOMAIN_OPTIONS, DOMAIN_META)} onChange={(v) => change({ domain: v })} />
            {hasPartner && (
              <SegmentedField label="Visibility" testid="detail-visibility" value={e.visibility || "private"}
                options={opts(VISIBILITY_OPTIONS, VISIBILITY_META)} onChange={(v) => change({ visibility: v })} />
            )}
          </div>

          {/* Snooze chips (revealed) */}
          {snoozeOpen && !done && (
            <div className="rise-in pt-1">
              <SnoozeChips entryId={e.id} onSnooze={(iso) => { change({ suggested_datetime: iso, tz_offset: tz() }, { suggested_datetime: iso, timing_status: "resolved", timing_phrase: null, needs_review: false }); setSnoozeOpen(false); }} />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-[var(--line2)]">
            <button
              data-testid="detail-done-btn"
              onClick={() => change({ status: done ? "ACTIVE" : "DONE" })}
              title={done ? "Reopen this item and move it back to active" : "Mark this item done"}
              aria-label={done ? "Reopen item" : "Mark done"}
              className={`${actionBtn} ${done
                ? "bg-[var(--pine-soft)] text-[var(--pine)] border border-[var(--pine-line)]"
                : "bg-[var(--pine)] text-[var(--on-pine)] hover:bg-[var(--pine-dark)] shadow-[0_6px_18px_rgba(44,90,77,0.22)]"}`}
            >
              {done ? <Undo2 size={15} /> : <Check size={15} strokeWidth={2} />}
              {done ? "Reopen" : "Mark done"}
            </button>

            {!done && (
              <button
                data-testid="detail-snooze-btn"
                onClick={() => setSnoozeOpen((s) => !s)}
                className={`${actionBtn} bg-[var(--surface)] text-[var(--ink2)] border border-[var(--line)] hover:border-[var(--pine)] hover:text-[var(--pine)]`}
              >
                <Clock size={14} strokeWidth={2} /> Snooze
              </button>
            )}

            <button
              data-testid="detail-delete-btn"
              onClick={() => onDelete(entry.id)}
              className={`${actionBtn} ml-auto bg-[var(--surface)] text-[var(--danger)] border border-[var(--danger-soft)] hover:bg-[var(--danger-soft)]`}
            >
              <Trash2 size={14} strokeWidth={2} /> Delete
            </button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
